import {DriveEntry, DriveFolder} from '@app/drive/files/drive-entry';
import {DriveApiIndexParams} from '@app/drive/files/queries/use-active-page-entries';
import type {UserFolderPathParams} from '@app/drive/files/queries/use-folder-path';
import type {
  UserFoldersApiParams,
  UserFoldersResponse,
} from '@app/drive/files/queries/use-folders';
import type {FetchStorageSummaryResponse} from '@app/drive/layout/sidebar/storage-summary/storage-summary';
import type {FetchShareableLinkResponse} from '@app/drive/shareable-link/queries/use-entry-shareable-link';
import {
  hasNextPage,
  LengthAwarePaginationResponse,
} from '@common/http/backend-response/pagination-response';
import {queryFactoryHelpers} from '@common/http/queries-file-helpers';
import {getSettingsPreviewMode} from '@common/admin/settings/preview/use-settings-preview-mode';
import {infiniteQueryOptions, queryOptions} from '@tanstack/react-query';
import {BootstrapData} from '@ui/bootstrap-data/bootstrap-data';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

const get = queryFactoryHelpers.get;

// the locale the page was server-rendered with; the bootstrap landingPage
// loader only matches this locale (used to gate initialData below)
const bootstrapActiveLocale = getBootstrapData().i18n?.active;

export const driveInvalidateKey = ['drive'];

export const appQueries = {
  driveEntries: {
    invalidateKey: [...driveInvalidateKey, 'entries'],
    index: <T = DriveEntry>(params: DriveApiIndexParams) =>
      infiniteQueryOptions({
        queryKey: [...driveInvalidateKey, 'entries', 'index', params],
        queryFn: ({pageParam = 1, signal}) => {
          return get<LengthAwarePaginationResponse<T> & {folder?: DriveFolder}>(
            'drive/file-entries',
            {
              ...params,
              page: pageParam,
            },
            signal,
          );
        },
        initialPageParam: 1,
        getNextPageParam: lastResponse => {
          const currentPage = lastResponse.current_page;
          if (!hasNextPage(lastResponse)) {
            return undefined;
          }
          return currentPage + 1;
        },
      }),
    get: (id: number) =>
      queryOptions<{fileEntry: DriveEntry}>({
        queryKey: [...driveInvalidateKey, 'entries', 'get', id],
        queryFn: () => get(`drive/file-entries/${id}/model`),
      }),
  },
  storageSummary: {
    invalidateKey: [...driveInvalidateKey, 'storage-summary'],
    // quota is now scoped to the active workspace's owner — caller must pass
    // the active workspaceId from React state so the queryKey changes
    // reactively when the user switches workspaces
    get: (workspaceId: number | null | undefined) =>
      queryOptions<FetchStorageSummaryResponse>({
        queryKey: [...driveInvalidateKey, 'storage-summary', workspaceId ?? 0],
        queryFn: () => get(`user/space-usage`),
      }),
  },
  folderPath: {
    invalidateKey: [...driveInvalidateKey, 'folders', 'path'],
    get: (hash: string, params: UserFolderPathParams = {}) =>
      queryOptions<{path: DriveFolder[]}>({
        queryKey: ['folder-path', hash, params],
        queryFn: () => get(`folders/${hash}/path`, params),
      }),
  },
  userFolders: {
    invalidateKey: [...driveInvalidateKey, 'folders', 'user'],
    index: (params: UserFoldersApiParams) =>
      queryOptions<UserFoldersResponse>({
        queryKey: ['user-folders', params],
        queryFn: () => get(`users/${params.userId}/folders`, params),
      }),
  },
  shareableLinks: {
    invalidateKey: ['shareable-links'],
    getEntryShareableLink: (entryId: number) =>
      queryOptions<FetchShareableLinkResponse>({
        queryKey: ['shareable-links', 'get-entry', entryId],
        queryFn: () =>
          get(`file-entries/${entryId}/shareable-link`, {
            loader: 'shareableLink',
          }),
      }),
  },
  landingPageData: {
    // `locale` is the active site locale; it is part of the query key so that
    // switching language live (without a full reload) refetches the
    // server-resolved, per-locale sections. The endpoint itself resolves the
    // language from the persisted user/cookie locale, not from a param.
    get: (locale?: string) => {
      // inside the settings-preview iframe, request the base (untranslated)
      // sections so the preview reflects the locale the admin is editing
      // (driven by the live form) rather than the admin's own account locale
      const isPreview = getSettingsPreviewMode().isInsideSettingsPreview;
      return queryOptions<
        Required<Required<BootstrapData>['loaders']>['landingPage']
      >({
        queryKey: ['landing-page-data', isPreview, locale ?? null],
        queryFn: () =>
          get('landing-page-data', isPreview ? {settingsPreview: 'true'} : {}),
        initialData: () => {
          // the server-rendered bootstrap loader only matches the locale the
          // page was originally loaded with; for any other locale force a
          // refetch instead of seeding stale (wrong-language) content
          if (locale && locale !== bootstrapActiveLocale) {
            return undefined;
          }
          const data = getBootstrapData().loaders?.landingPage;
          if (data) {
            return data;
          }
        },
      });
    },
  },
};
